import { useState, useCallback } from "react";
import type { ScopeOfWorkData, ItemizedRoomPriceBreakdown } from "@rebuild/shared";
import { formatCurrencyInput, transformScopeOfWorkToString } from "@rebuild/shared";

type EditingTarget = {
  type: "room" | "category" | "item";
  field: "text" | "price" | "quantity";
  roomIndex: number;
  categoryIndex?: number;
  itemIndex?: number;
};

interface UseInlineEditingProps {
  editableScopeOfWork: ScopeOfWorkData | null;
  setEditableScopeOfWork: (data: ScopeOfWorkData) => void;
  onSubtotalChange: (subtotal: string) => void;
  onScopeNoteTextChange: (text: string) => void;
}

export function useInlineEditing({
  editableScopeOfWork,
  setEditableScopeOfWork,
  onSubtotalChange,
  onScopeNoteTextChange,
}: UseInlineEditingProps) {
  const [editingItem, setEditingItem] = useState<EditingTarget | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const startEditing = useCallback(
    (
      type: EditingTarget["type"],
      field: EditingTarget["field"],
      roomIndex: number,
      categoryIndex?: number,
      itemIndex?: number,
      currentValue?: string | number,
    ) => {
      setEditingItem({ type, field, roomIndex, categoryIndex, itemIndex });
      // For prices, strip the $ and format for editing
      if (field === "price" && typeof currentValue === "number") {
        setEditingValue(currentValue.toFixed(2));
      } else {
        setEditingValue(String(currentValue || ""));
      }
    },
    [],
  );

  const saveEdit = useCallback(() => {
    if (!editingItem || !editableScopeOfWork) return;

    if (editableScopeOfWork.type === "room-grouped") {
      const updatedData = { ...editableScopeOfWork };
      const rooms: ItemizedRoomPriceBreakdown[] = [...updatedData.scopeOfWorkRooms];

      // Parse value based on field type
      const newValue = editingItem.field === "text" ? editingValue : parseFloat(editingValue) || 0;

      if (editingItem.type === "room") {
        if (editingItem.field === "text") {
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], room: newValue as string };
        } else {
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], price: newValue as number };
        }
      } else if (editingItem.type === "category" && editingItem.categoryIndex !== undefined) {
        const categories = [...rooms[editingItem.roomIndex].categories];
        if (editingItem.field === "text") {
          categories[editingItem.categoryIndex] = { ...categories[editingItem.categoryIndex], category: newValue as string };
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], categories };
        } else {
          // Update category price
          categories[editingItem.categoryIndex] = { ...categories[editingItem.categoryIndex], price: newValue as number };
          // Recalculate room price (sum of all categories)
          const roomPrice = categories.reduce((sum, cat) => sum + cat.price, 0);
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], categories, price: roomPrice };
        }
      } else if (editingItem.type === "item" && editingItem.categoryIndex !== undefined && editingItem.itemIndex !== undefined) {
        const categories = [...rooms[editingItem.roomIndex].categories];
        const items = [...categories[editingItem.categoryIndex].items];
        if (editingItem.field === "text") {
          items[editingItem.itemIndex] = { ...items[editingItem.itemIndex], item: newValue as string };
          categories[editingItem.categoryIndex] = { ...categories[editingItem.categoryIndex], items };
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], categories };
        } else if (editingItem.field === "quantity") {
          // The quantity is the measurement, so the line item price is re-derived from it rather
          // than being an independent override the way a directly edited price is.
          const quantity = newValue as number;
          const editedItem = items[editingItem.itemIndex];
          items[editingItem.itemIndex] = { ...editedItem, quantityValue: quantity, price: editedItem.unitPrice * quantity };
          const categoryPrice = items.reduce((sum, item) => sum + item.price, 0);
          categories[editingItem.categoryIndex] = { ...categories[editingItem.categoryIndex], items, price: categoryPrice };
          const roomPrice = categories.reduce((sum, cat) => sum + cat.price, 0);
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], categories, price: roomPrice };
        } else {
          // Update item price
          items[editingItem.itemIndex] = { ...items[editingItem.itemIndex], price: newValue as number };
          // Recalculate category price (sum of all items)
          const categoryPrice = items.reduce((sum, item) => sum + item.price, 0);
          categories[editingItem.categoryIndex] = { ...categories[editingItem.categoryIndex], items, price: categoryPrice };
          // Recalculate room price (sum of all categories)
          const roomPrice = categories.reduce((sum, cat) => sum + cat.price, 0);
          rooms[editingItem.roomIndex] = { ...rooms[editingItem.roomIndex], categories, price: roomPrice };
        }
      }

      // Recalculate subtotal (sum of all rooms) when any price changes
      if (editingItem.field === "price" || editingItem.field === "quantity") {
        const newSubtotal = rooms.reduce((sum, room) => sum + room.price, 0);
        onSubtotalChange(formatCurrencyInput(newSubtotal.toFixed(2)));
      }

      updatedData.scopeOfWorkRooms = rooms;
      setEditableScopeOfWork(updatedData);
      // Update the text representation as well
      onScopeNoteTextChange(transformScopeOfWorkToString(updatedData));
    }

    setEditingItem(null);
    setEditingValue("");
  }, [editingItem, editableScopeOfWork, editingValue, setEditableScopeOfWork, onSubtotalChange, onScopeNoteTextChange]);

  const cancelEdit = useCallback(() => {
    setEditingItem(null);
    setEditingValue("");
  }, []);

  const isEditing = useCallback(
    (
      type: EditingTarget["type"],
      field: EditingTarget["field"],
      roomIndex: number,
      categoryIndex?: number,
      itemIndex?: number,
    ): boolean => {
      if (!editingItem) return false;
      return (
        editingItem.type === type &&
        editingItem.field === field &&
        editingItem.roomIndex === roomIndex &&
        editingItem.categoryIndex === categoryIndex &&
        editingItem.itemIndex === itemIndex
      );
    },
    [editingItem],
  );

  return {
    editingItem,
    editingValue,
    setEditingValue,
    startEditing,
    saveEdit,
    cancelEdit,
    isEditing,
  };
}
